import React, { useEffect, useRef, useState } from "react";
import { api, formatError } from "../../api";
import {
  Briefcase, Building2, IndianRupee, MapPin, Calendar, Sparkles, Upload, X, Plus,
  Code2, ShieldCheck, ImagePlus, CheckCircle2, AlertTriangle, Users,
} from "lucide-react";

/* ─────────────────────────── constants ──────────────────────────────────── */

const TYPE_OPTIONS = [
  { value: "internship", label: "Internship",      hint: "Time-bound learning role" },
  { value: "project",    label: "Project",         hint: "Fixed-scope deliverable" },
  { value: "permanent",  label: "Full-time / Permanent", hint: "Long-term employment" },
  { value: "contract",   label: "Contract",        hint: "Fixed-term engagement" },
];

const CATEGORY_OPTIONS = [
  { value: "coe",   label: "Technical (CoE)",     hint: "Map to one of the 10 SkillConnect CoE domains" },
  { value: "other", label: "Other / Non-Technical", hint: "BD, Sales, Ops, Finance, HR, Partnerships, … bypasses CoE check" },
];

const EXP_OPTIONS = [
  { value: "0",  label: "Fresher (0 yrs)" },
  { value: "1",  label: "1 yr" },
  { value: "2",  label: "2 yrs" },
  { value: "3",  label: "3 yrs" },
  { value: "5",  label: "5 yrs" },
  { value: "8",  label: "8 yrs" },
  { value: "10", label: "10 yrs" },
  { value: "15", label: "15+ yrs" },
];

const SALARY_PERIODS = [
  { value: "monthly", label: "per month" },
  { value: "annual",  label: "per year"  },
  { value: "stipend", label: "stipend"   },
  { value: "hourly",  label: "per hour"  },
];

const SUGGESTED_PERKS = [
  "Office cab / shuttle", "Food allowance", "Health insurance", "Remote-friendly",
  "Flexible hours", "ESOPs / Stock", "Performance bonus", "Annual hike",
  "Wellness programs", "Learning budget", "Paid parental leave", "Relocation support",
];

/* ─────────────────────────── small bits ─────────────────────────────────── */

function Section({ icon: Icon, title, hint, children }) {
  return (
    <section className="card card-pad space-y-4">
      <div className="flex items-start gap-2.5">
        <div className="w-9 h-9 rounded-lg grid place-items-center bg-blue-50 text-signal shrink-0">
          <Icon size={16} />
        </div>
        <div className="min-w-0">
          <h3 className="font-display font-bold text-lg leading-tight">{title}</h3>
          {hint && <p className="text-xs text-muted mt-0.5">{hint}</p>}
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-full border-2 border-dashed text-xs font-semibold transition-all
        ${active
          ? "bg-signal/10 border-signal text-signal"
          : "border-rule text-muted hover:border-signal hover:text-signal"}`}
    >
      {children}
    </button>
  );
}

/* ─────────────────────────── main form ──────────────────────────────────── */

const EMPTY = {
  title: "",
  description: "",
  type: "permanent",
  category: "coe",      // coe | other  (other = bypass CoE governance)
  skills_required: "",
  experience_min: "0",
  experience_max: "1",
  salary_min: "",
  salary_max: "",
  salary_period: "monthly",
  openings: 1,
  location: "",
  deadline: "",
  perks: [],
};

/**
 * Reusable form for both creating and editing a job posting.
 *
 * Props:
 *   editing  – optional posting object. When provided, the form pre-fills with
 *              its values and submits a PATCH instead of POST.
 *   onSaved  – callback fired after a successful save (used by edit-modal).
 */
export default function NewPostingForm({ editing = null, onSaved = null }) {
  const seed = editing
    ? {
        title:        editing.title || "",
        description:  editing.description || "",
        type:         editing.type || "permanent",
        category:     (editing.governance_override === "OTHER" || editing.sc_domain === "OTHER")
                        ? "other" : "coe",
        skills_required: (editing.skills_required || []).join(", "),
        experience_min: String(editing.experience_min ?? "0"),
        experience_max: String(editing.experience_max ?? "1"),
        salary_min: editing.salary_min == null ? "" : String(editing.salary_min),
        salary_max: editing.salary_max == null ? "" : String(editing.salary_max),
        salary_period: editing.salary_period || "monthly",
        openings: editing.openings || 1,
        location: editing.location || "",
        deadline: editing.deadline ? String(editing.deadline).slice(0, 10) : "",
        perks: editing.perks || [],
      }
    : EMPTY;
  const [f, setF] = useState(seed);
  const [msg, setMsg] = useState("");
  const [gov, setGov] = useState(null);
  const [perkSearch, setPerkSearch] = useState("");
  const [coverPreview, setCoverPreview] = useState(editing?.cover_image || "");
  const [coverFile, setCoverFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const isEdit = !!editing;
  const isOther = f.category === "other";

  const setField = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // Live governance classifier (600 ms debounce) — skipped for non-technical.
  useEffect(() => {
    if (isOther) { setGov(null); return; }
    if (!f.title.trim() && !f.description.trim()) { setGov(null); return; }
    const t = setTimeout(() => {
      api.get("/governance/classify", {
        params: { title: f.title, description: f.description, skills: f.skills_required },
      }).then((r) => setGov(r.data)).catch(() => setGov(null));
    }, 600);
    return () => clearTimeout(t);
  }, [f.title, f.description, f.skills_required, isOther]);

  const addPerk = (p) => { if (!f.perks.includes(p)) setF((s) => ({ ...s, perks: [...s.perks, p] })); setPerkSearch(""); };
  const removePerk = (p) => setF((s) => ({ ...s, perks: s.perks.filter((x) => x !== p) }));

  const pickCover = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 4 * 1024 * 1024) { setMsg("Cover image too large (max 4 MB)."); return; }
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const clearCover = async () => {
    // Local-only clear when creating, or before file is picked.
    setCoverFile(null);
    setCoverPreview("");
    if (fileRef.current) fileRef.current.value = "";
    // When editing an existing posting that already has a server-side cover,
    // also delete it on the server.
    if (isEdit && editing?.cover_image) {
      try {
        await api.delete(`/postings/${editing.id}/cover`);
        if (onSaved) onSaved({ ...editing, cover_image: null });
      } catch (err) { setMsg(formatError(err)); }
    }
  };

  const reset = () => { setF(EMPTY); setGov(null); clearCover(); };

  const submit = async (e) => {
    e.preventDefault(); setMsg(""); 
    const skills = f.skills_required.split(",").map((s) => s.trim()).filter(Boolean);
    const desc = f.description.trim();
    // Required for AI matching + classification — mirror the backend rules so
    // the recruiter gets a friendly message instead of a 422.
    if (skills.length < 3) {
      setMsg("Please add at least 3 skills (comma-separated) so our AI can match the right candidates to this role.");
      return;
    }
    if (desc.length < 40) {
      setMsg("Please write a job description of at least 40 characters so the role is clear and the AI can classify it.");
      return;
    }
    setBusy(true);
    const payload = {
      title: f.title.trim(),
      description: desc,
      type: f.type,
      skills_required: skills,
      experience_min: f.experience_min === "" ? null : Number(f.experience_min),
      experience_max: f.experience_max === "" ? null : Number(f.experience_max),
      salary_min: f.salary_min === "" ? null : Number(f.salary_min),
      salary_max: f.salary_max === "" ? null : Number(f.salary_max),
      salary_period: f.salary_period,
      salary_currency: "INR",
      openings: Number(f.openings) || 1,
      location: f.location.trim() || null,
      deadline: f.deadline || null,
      perks: f.perks,
      coe_domain_override: isOther ? "OTHER" : "",
    };
    try {
      let pid;
      if (isEdit) {
        const r = await api.patch(`/postings/${editing.id}`, payload);
        pid = editing.id;
        if (coverFile) {
          const fd = new FormData(); fd.append("file", coverFile);
          await api.post(`/postings/${pid}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        setMsg("✅ Updated successfully");
        if (onSaved) onSaved(r.data);
      } else {
        const r = await api.post("/postings", payload);
        pid = r.data?.id;
        if (coverFile && pid) {
          const fd = new FormData(); fd.append("file", coverFile);
          await api.post(`/postings/${pid}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        setMsg("✅ Posted successfully");
        reset();
      }
    } catch (e2) { setMsg(formatError(e2)); }
    finally { setBusy(false); }
  };

  const filteredPerks = perkSearch
    ? SUGGESTED_PERKS.filter((p) => p.toLowerCase().includes(perkSearch.toLowerCase()))
    : SUGGESTED_PERKS;
  const visiblePerks = filteredPerks.filter((p) => !f.perks.includes(p)).slice(0, 12);
  const blockedByGov = !isOther && !!(gov && !gov.allowed);

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="new-posting-form">
      {/* ───── Job basics ────────────────────────────────────────────────── */}
      <Section icon={Briefcase} title="Job basics" hint="What and where you're hiring for.">
        <div>
          <label className="eyebrow">Job title</label>
          <input
            required value={f.title} onChange={(e) => setField("title", e.target.value)}
            className="input mt-2" placeholder="e.g. IT Service Manager · Senior ML Engineer"
            data-testid="post-title"
          />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="eyebrow">Employment type</label>
            <select
              className="input mt-2" value={f.type}
              onChange={(e) => setField("type", e.target.value)}
              data-testid="post-type"
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <p className="text-[11px] text-muted mt-1">{TYPE_OPTIONS.find((o) => o.value === f.type)?.hint}</p>
          </div>
          <div>
            <label className="eyebrow">Openings</label>
            <input
              type="number" min="1" max="500" className="input mt-2"
              value={f.openings} onChange={(e) => setField("openings", e.target.value)}
              data-testid="post-openings"
            />
          </div>
        </div>
      </Section>

      {/* ───── Domain category (governance gate) ──────────────────────── */}
      <Section icon={ShieldCheck} title="Domain category" hint="Technical roles map to one of the 10 CoE domains. Pick 'Other' to publish a non-technical role.">
        <div className="grid sm:grid-cols-2 gap-3">
          {CATEGORY_OPTIONS.map((o) => (
            <label
              key={o.value}
              className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all
                ${f.category === o.value
                  ? "border-signal bg-signal/5"
                  : "border-rule hover:border-signal/50"}`}
              data-testid={`post-category-${o.value}`}
            >
              <input
                type="radio" name="post-category" value={o.value}
                checked={f.category === o.value}
                onChange={() => setField("category", o.value)}
                className="mt-1"
              />
              <div className="min-w-0">
                <div className="font-semibold text-sm">{o.label}</div>
                <div className="text-xs text-muted mt-0.5">{o.hint}</div>
              </div>
            </label>
          ))}
        </div>
      </Section>

      {/* ───── Work experience ──────────────────────────────────────────── */}
      <Section icon={Users} title="Work experience" hint="Pick the years-of-experience window candidates should fit.">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <select className="input" value={f.experience_min} onChange={(e) => setField("experience_min", e.target.value)} data-testid="post-exp-min">
            {EXP_OPTIONS.map((o) => <option key={o.value} value={o.value}>Min · {o.label}</option>)}
          </select>
          <span className="text-muted text-sm">to</span>
          <select className="input" value={f.experience_max} onChange={(e) => setField("experience_max", e.target.value)} data-testid="post-exp-max">
            {EXP_OPTIONS.map((o) => <option key={o.value} value={o.value}>Max · {o.label}</option>)}
          </select>
        </div>
      </Section>

      {/* ───── Salary ───────────────────────────────────────────────────── */}
      <Section icon={IndianRupee} title="Salary / package" hint="Numeric range. Leave blank to mark as undisclosed.">
        <div className="grid md:grid-cols-[1fr_auto_1fr_150px] items-center gap-3">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none">₹</span>
            <input
              type="number" min="0" className="input pl-7" placeholder="Min"
              value={f.salary_min} onChange={(e) => setField("salary_min", e.target.value)}
              data-testid="post-salary-min"
            />
          </div>
          <span className="text-muted text-sm">to</span>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none">₹</span>
            <input
              type="number" min="0" className="input pl-7" placeholder="Max"
              value={f.salary_max} onChange={(e) => setField("salary_max", e.target.value)}
              data-testid="post-salary-max"
            />
          </div>
          <select
            className="input" value={f.salary_period}
            onChange={(e) => setField("salary_period", e.target.value)}
            data-testid="post-salary-period"
          >
            {SALARY_PERIODS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </Section>

      {/* ───── Location & deadline ───────────────────────────────────── */}
      <Section icon={MapPin} title="Where & when" hint="Job location and application deadline.">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="eyebrow">Location</label>
            <input
              className="input mt-2" placeholder="e.g. Pune · Remote · Hybrid (Bangalore)"
              value={f.location} onChange={(e) => setField("location", e.target.value)}
              data-testid="post-location"
            />
          </div>
          <div>
            <label className="eyebrow flex items-center gap-1.5"><Calendar size={11} /> Application deadline</label>
            <input
              type="date" className="input mt-2"
              value={f.deadline} onChange={(e) => setField("deadline", e.target.value)}
              data-testid="post-deadline"
            />
          </div>
        </div>
      </Section>

      {/* ───── Skills ───────────────────────────────────────────────────── */}
      <Section icon={Code2} title="Skills required *" hint="Comma-separated, at least 3. Powers AI candidate matching + CoE classification.">
        <input
          className="input" placeholder="e.g. Python, TensorFlow, Docker, AWS"
          value={f.skills_required} onChange={(e) => setField("skills_required", e.target.value)}
          data-testid="post-skills"
        />
      </Section>

      {/* ───── Perks ────────────────────────────────────────────────────── */}
      <Section icon={Sparkles} title={<>Perks &amp; benefits <span className="text-xs text-muted font-normal">(optional)</span></>}
               hint="Help your listing stand out. Tap to add, click again to remove.">
        <input
          className="input" placeholder="Search perks…" value={perkSearch}
          onChange={(e) => setPerkSearch(e.target.value)} data-testid="post-perks-search"
        />

        {f.perks.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="post-perks-selected">
            {f.perks.map((p) => (
              <span key={p} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-signal text-white text-xs font-semibold">
                <CheckCircle2 size={12} /> {p}
                <button type="button" onClick={() => removePerk(p)} className="hover:opacity-70" aria-label={`Remove ${p}`}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        {visiblePerks.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-muted mb-2">Suggested perks</div>
            <div className="flex flex-wrap gap-2" data-testid="post-perks-suggested">
              {visiblePerks.map((p) => (
                <Chip key={p} active={false} onClick={() => addPerk(p)} testid={`perk-suggest-${p}`}>
                  <span className="inline-flex items-center gap-1"><Plus size={12} /> {p}</span>
                </Chip>
              ))}
            </div>
          </div>
        )}
      </Section>

      {/* ───── Cover image / badge ──────────────────────────────────── */}
      <Section icon={ImagePlus} title="Job photo / badge" hint="Optional cover image shown on the public posting card. PNG/JPG/WebP up to 4 MB.">
        <div className="flex items-start gap-4 flex-wrap">
          <div
            className="w-40 h-28 rounded-xl bg-paper border-2 border-dashed flex items-center justify-center overflow-hidden shrink-0"
            style={{ borderColor: "var(--rule)" }}
          >
            {coverPreview ? (
              <img src={coverPreview} alt="Cover preview" className="w-full h-full object-cover" />
            ) : (
              <div className="text-muted text-xs flex flex-col items-center gap-1">
                <ImagePlus size={20} /> No image
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input
              type="file" accept="image/png,image/jpeg,image/webp" ref={fileRef}
              onChange={pickCover} className="hidden" data-testid="post-cover-input"
            />
            <button type="button" className="btn-ghost" onClick={() => fileRef.current?.click()} data-testid="post-cover-pick">
              <Upload size={14} /> {coverPreview ? "Replace photo" : "Upload photo / badge"}
            </button>
            {coverPreview && (
              <button type="button" className="text-xs text-red-600 hover:underline self-start" onClick={clearCover} data-testid="post-cover-clear">
                Remove
              </button>
            )}
          </div>
        </div>
      </Section>

      {/* ───── Description ──────────────────────────────────────────────── */}
      <Section icon={Building2} title="Job description *" hint="At least 40 characters. Roles, responsibilities, tooling, eligibility — markdown supported.">
        <textarea
          required className="input min-h-[180px]" placeholder="Describe the role, the team, what success looks like in 90 days, perks specific to the team…"
          value={f.description} onChange={(e) => setField("description", e.target.value)}
          data-testid="post-desc"
        />
      </Section>

      {/* ───── Governance & submit ──────────────────────────────────────── */}
      {isOther ? (
        <div
          className="rounded-xl px-4 py-3 border border-amber-200 bg-amber-50 text-amber-900 text-sm flex items-start gap-2.5"
          data-testid="post-governance"
        >
          <ShieldCheck size={16} className="shrink-0 mt-0.5" />
          <div>
            <b>Non-technical role</b> — CoE governance bypassed.
            <div className="text-xs opacity-80 mt-0.5">This posting will be tagged as <code>OTHER</code> and won&apos;t appear in CoE-domain filters.</div>
          </div>
        </div>
      ) : gov && (
        <div
          className={`rounded-xl px-4 py-3 border text-sm flex items-start gap-2.5 ${
            gov.allowed ? "border-green-200 bg-green-50 text-green-900"
                        : "border-red-200 bg-red-50 text-red-900"
          }`}
          data-testid="post-governance"
        >
          {gov.allowed ? <ShieldCheck size={16} className="shrink-0 mt-0.5" /> : <AlertTriangle size={16} className="shrink-0 mt-0.5" />}
          <div>
            {gov.allowed ? (
              <>
                <b>{gov.sc_domain}</b> — {gov.sc_domain_name}
                {gov.matched_role ? <> · {gov.matched_role}</> : null} · governance score {gov.score}
              </>
            ) : (
              <>
                <b>Governance: blocked</b> — {gov.reason}
                <div className="text-xs opacity-80 mt-0.5">Switch to <b>Other / Non-Technical</b> above to publish anyway, or rewrite to align with one of the 10 SkillConnect CoE domains.</div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-sm text-muted" data-testid="post-msg">{msg}</span>
        <div className="flex items-center gap-2">
          {!isEdit && (
            <button type="button" className="btn-ghost" onClick={reset} disabled={busy} data-testid="post-reset">
              Reset
            </button>
          )}
          <button
            type="submit"
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={busy || blockedByGov}
            data-testid="post-submit"
          >
            {busy ? (isEdit ? "Saving…" : "Publishing…") : (isEdit ? "Save changes" : "Publish job")}
          </button>
        </div>
      </div>
    </form>
  );
}
